import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/projects", label: "Projects" },
  { to: "/consultation", label: "Consultation" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "bg-bone/90 backdrop-blur-md border-b border-borderwarm"
          : "bg-transparent"
      }`}
    >
      <div className="container-luxury flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-baseline gap-2 group"
          aria-label="Auraa Designs Home"
        >
          <span className="font-serif text-2xl md:text-[26px] tracking-tight text-espresso group-hover:text-oxblood transition-colors duration-500">
            Auraa
          </span>
          <span className="text-[10px] uppercase tracking-[0.32em] text-espresso-soft hidden sm:inline">
            Designs
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 lg:gap-11">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              end={l.to === "/"}
              className={({ isActive }) =>
                `text-[11px] uppercase tracking-[0.24em] font-medium transition-colors duration-500 ${
                  isActive ? "text-oxblood" : "text-espresso hover:text-oxblood"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block">
          <a
            href="https://calendly.com/archanaura8/30min"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-cta"
            className="btn-primary text-[10px] !px-6 !py-3"
          >
            Book Consultation
          </a>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 -mr-2 text-espresso"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <div
        className={`md:hidden overflow-hidden transition-[max-height] duration-500 ${
          open ? "max-h-[420px]" : "max-h-0"
        }`}
      >
        <div className="container-luxury pb-8 pt-2 flex flex-col gap-5">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              end={l.to === "/"}
              className={({ isActive }) =>
                `text-sm uppercase tracking-[0.24em] ${
                  isActive ? "text-oxblood" : "text-espresso"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href="https://calendly.com/archanaura8/30min"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-header-cta"
            className="btn-primary !text-[11px] !px-7 !py-3 self-start mt-2"
          >
            Book Consultation
          </a>
        </div>
      </div>
    </header>
  );
}
